/**
 * Plan tiers and their limits — pure data, no environment access.
 *
 * Split out of `plan-config.ts` so surfaces that cannot evaluate a Worker
 * environment can still read the tier contract. `@postdaily/env/server`
 * resolves `cloudflareEnv` at module scope, so importing `plan-config.ts`
 * from the web bundle (or from a plain Node test) throws before any code
 * runs — which is why `apps/web` had begun re-declaring constants like
 * `UNLIMITED` by hand.
 *
 * `plan-config.ts` re-exports everything here, so existing imports of it are
 * unchanged; reach for THIS module from anything that is not a Worker.
 */

import type { CheckoutPlanCode, PlanCode } from "../schemas/billing";

export type PlanLimits = {
  adminSupportLevel: "community" | "priority" | "agency";
  // Analytics CSV export (posts / daily / channels / tags datasets). Sold as a
  // Pro-and-up line on the pricing page, so it is a hard tier gate rather than
  // a quantitative limit.
  analyticsCsvExport: boolean;
  /**
   * How far back analytics reads may look, in days. Enforced as a READ window,
   * never as a delete: rows outlive the window (maintenance prunes on its own
   * flat schedule), so upgrading immediately reveals the full history instead
   * of recovering nothing. Every analytics range is clamped to
   * `now - analyticsRetentionDays` server-side — see `resolveAnalyticsWindow`.
   */
  analyticsRetentionDays: number;
  // Client approval workflows + tokenized review portal. The first
  // feature-based (non-quantitative) tier gate: Pro and Agency only.
  approvalWorkflows: boolean;
  // Workspace audit log (settings → Audit). Agency-only, matching the tier that
  // sells it; lower tiers get a locked upsell rather than a missing page.
  auditLog: boolean;
  maxAiCreditsPerMonth: number;
  maxMediaFileBytes: number;
  maxMediaStorageBytes: number;
  maxPostMediaItems: number;
  maxPostTargets: number;
  maxScheduledPostsPerMonth: number;
  maxSeats: number;
  maxSocialAccounts: number;
  maxWorkspaces: number;
  /**
   * Monthly X (Twitter) API spend budget, in credits where 1 credit = $0.001.
   * The number here IS the worst-case dollar exposure per subscriber per month:
   * `maxXCreditsPerMonth x $0.001`. Pooled across every workspace the owner
   * holds, so one subscription buys exactly one ceiling.
   *
   * Not a post count on purpose. X bills per call — $0.015 a create, $0.20 if
   * the post contains a link, $0.005 a metrics read — and one scheduled post
   * can be a 25-segment thread, so a post-denominated cap understates real
   * spend by up to 25x (and 13x again on links).
   *
   * Rough conversions at these rates: a plain post costs ~70 credits over its
   * life (15 to create + ~55 of metrics reads across 14 days), a post with a
   * link ~255. Retune against your live plan prices — the ceiling should stay
   * a comfortable fraction of what the tier charges. 0 = no X access.
   */
  maxXCreditsPerMonth: number;
  providerAccessFlags: {
    agencyProviders: boolean;
    realProviders: boolean;
    // Whether this plan may connect (and post to) X / Twitter. X has no free
    // posting tier — we post through our own X app and pay per post — so free
    // X posting costs us money. Gated to paid plans; false on Free.
    xProvider: boolean;
  };
};

export type PlanConfig = {
  code: PlanCode;
  description: string;
  limits: PlanLimits;
  name: string;
  polarProductId?: string;
  upgradePlanCode?: CheckoutPlanCode;
};

const mb = 1024 * 1024;
const gb = 1024 * mb;

/**
 * Sentinel for an effectively-unlimited numeric limit. Large enough that the
 * `usage + delta > limit` entitlement checks never trip, small enough to
 * serialize as a normal JSON number (unlike Infinity). The UI treats any limit
 * `>= UNLIMITED` as "Unlimited" rather than rendering the raw number.
 */
export const UNLIMITED = 1_000_000_000;

export function isUnlimited(limit: number) {
  return limit >= UNLIMITED;
}

export const planConfigs: Record<PlanCode, PlanConfig> = {
  free: {
    code: "free",
    description: "For evaluation and solo light usage.",
    name: "Free",
    upgradePlanCode: "starter",
    limits: {
      adminSupportLevel: "community",
      analyticsCsvExport: false,
      analyticsRetentionDays: 7,
      approvalWorkflows: false,
      auditLog: false,
      maxAiCreditsPerMonth: 5,
      maxMediaFileBytes: 25 * mb,
      maxMediaStorageBytes: 250 * mb,
      maxPostMediaItems: 4,
      maxPostTargets: 3,
      maxScheduledPostsPerMonth: 5,
      maxSeats: 1,
      maxSocialAccounts: 1,
      maxWorkspaces: 1,
      maxXCreditsPerMonth: 0,
      providerAccessFlags: {
        agencyProviders: false,
        realProviders: true,
        xProvider: false,
      },
    },
  },
  starter: {
    code: "starter",
    description: "For solo creators getting serious.",
    name: "Starter",
    upgradePlanCode: "pro",
    limits: {
      adminSupportLevel: "community",
      analyticsCsvExport: false,
      analyticsRetentionDays: 30,
      approvalWorkflows: false,
      auditLog: false,
      maxAiCreditsPerMonth: 50,
      maxMediaFileBytes: 50 * mb,
      maxMediaStorageBytes: 2 * gb,
      maxPostMediaItems: 6,
      maxPostTargets: 5,
      maxScheduledPostsPerMonth: UNLIMITED,
      maxSeats: 1,
      maxSocialAccounts: 3,
      maxWorkspaces: 1,
      maxXCreditsPerMonth: 3_000,
      providerAccessFlags: {
        agencyProviders: false,
        realProviders: true,
        xProvider: true,
      },
    },
  },
  pro: {
    code: "pro",
    description: "For creators and small teams.",
    name: "Pro",
    upgradePlanCode: "agency",
    limits: {
      adminSupportLevel: "priority",
      analyticsCsvExport: true,
      analyticsRetentionDays: 90,
      approvalWorkflows: true,
      auditLog: false,
      maxAiCreditsPerMonth: 500,
      maxMediaFileBytes: 100 * mb,
      maxMediaStorageBytes: 5 * gb,
      maxPostMediaItems: 8,
      maxPostTargets: 10,
      maxScheduledPostsPerMonth: UNLIMITED,
      maxSeats: 3,
      maxSocialAccounts: 10,
      maxWorkspaces: 3,
      maxXCreditsPerMonth: 12_000,
      providerAccessFlags: {
        agencyProviders: false,
        realProviders: true,
        xProvider: true,
      },
    },
  },
  agency: {
    code: "agency",
    description: "For client management.",
    name: "Agency",
    limits: {
      adminSupportLevel: "agency",
      analyticsCsvExport: true,
      analyticsRetentionDays: 365,
      approvalWorkflows: true,
      auditLog: true,
      maxAiCreditsPerMonth: 3000,
      maxMediaFileBytes: 250 * mb,
      maxMediaStorageBytes: 50 * gb,
      maxPostMediaItems: 12,
      maxPostTargets: 25,
      maxScheduledPostsPerMonth: UNLIMITED,
      maxSeats: 10,
      maxSocialAccounts: 100,
      maxWorkspaces: 25,
      maxXCreditsPerMonth: 50_000,
      providerAccessFlags: {
        agencyProviders: true,
        realProviders: true,
        xProvider: true,
      },
    },
  },
};

// Tier order, low → high. Used to compare plans by rank rather than by a single
// limit value (two tiers can share a limit — e.g. free and starter both allow 1
// workspace — so ranking, not the limit, decides which plan a user "has").
const PLAN_ORDER: PlanCode[] = ["free", "starter", "pro", "agency"];

/** Every tier strictly above `planCode`, cheapest first. */
export function plansAbove(planCode: PlanCode): PlanCode[] {
  return PLAN_ORDER.slice(planRank(planCode) + 1);
}

/** Tier position (free=0 … agency=3); unknown codes rank as free. */
export function planRank(planCode: PlanCode) {
  const index = PLAN_ORDER.indexOf(planCode);

  return index === -1 ? 0 : index;
}

/**
 * The aspirational upgrade target used to frame the billing page's plan
 * comparison and hero upsell. Unlike `upgradePlanCode` (strictly the next tier,
 * free→starter), this points free/starter users straight at Pro — the
 * best-value tier — and Pro users at Agency. Agency has nothing above it.
 *
 * This only frames the pitch: the "Upgrade" CTA opens the full plan dialog, so
 * users can still pick any tier (including Starter). Returns `null` when there
 * is nothing to upsell.
 */
export function getRecommendedUpgradeCode(
  planCode: PlanCode,
): CheckoutPlanCode | null {
  if (planCode === "free" || planCode === "starter") {
    return "pro";
  }

  if (planCode === "pro") {
    return "agency";
  }

  return null;
}
